import { createSlice } from "@reduxjs/toolkit"
import { buildFiducialInputSignature } from "../../utils/fiducialIntervals"
import { getPersistedScale, setPersistedScale } from "../../utils/persistedScale"
import { computeDefaultBeatType } from "../../utils/dominantBeatType"
import { DEFAULT_MEDIAN_BEAT_WINDOW } from "../../constants/annotator"
import {
	getPersistedMedianBeatSingleColumn,
	setPersistedMedianBeatSingleColumn,
} from "../../utils/persistedMedianBeatSingleColumn"
import {
	getPersistedMedianBeatVerticalSpacing,
	setPersistedMedianBeatVerticalSpacing,
} from "../../utils/persistedMedianBeatVerticalSpacing"

const initialState = {
	rawData: {},
	currentTask: {},
	currentTaskRecordings: [],
	currentRecordingId: null,
	original: {
		frequency: 0,
		mainsFrequency: 0,
		gain: 0,
		duration: 0,
		samples: {},
		filteredSamples: null,
		info: {},
	},
	filtered: {
		frequency: 0,
		mainsFrequency: 0,
		gain: 0,
		duration: 0,
		samples: {},
		filters: {
			adaptive: false,
			ButterworthLowPass: 0,
			ButterworthHighPass: 0,
			noSyn: false,
			viewMode: "filtered",
		},
		info: {},
	},
	annotations: {
		beats: [], // [offset, beatType]
		intervals: [],
		global: [],
		// Fiducial points bucketed by the beat-type slug the median beat was
		// computed from. Shape: { n: [{type, offsetMs}, ...], a: [...], ... }.
		// A missing key means "no points placed for that beat type yet."
		medianBeatFiducialPointsByType: {},
		labels: {
			beats: [],
			intervals: [],
			fiducialPoints: [],
		},
		labelsFiducialPoints: [],
		audit: [],
	},
	settings: {
		scale: getPersistedScale(),
		inverted: false,
		medianBeatSingleColumn: getPersistedMedianBeatSingleColumn(),
		// Vertical spacing (0..1) between per-lead median-beat tiles in
		// single-column mode. 1 = default spacing; 0 = tiles fully overlap
		// so every lead's y=0 baseline coincides on screen.
		medianBeatVerticalSpacing: getPersistedMedianBeatVerticalSpacing(),
		// Window settings bucketed by beat-type slug.
		// Missing keys fall back to DEFAULT_MEDIAN_BEAT_WINDOW.
		medianBeatWindowByType: {},
		// Which beat type the median beat is currently computed from.
		// Not persisted to localStorage — re-derived per recording via the
		// dominant-beat-type heuristic in setAnnotatorState.
		medianBeatBeatType: "n",
	},
	leadCount: 0,
	selectedLead: null,
	lastModifiedBeatIndex: null,
	context: {
		show: false,
		data: {
			top: 0,
			left: 0,
			showDelete: false,
			beatIndex: 0,
		},
	},
	fiducialPointContext: {
		show: false,
		data: {
			top: 0,
			left: 0,
			sampleIndex: null,
			existingType: null,
		},
	},
	intrervalMenu: {
		show: false,
		top: 0,
		left: 0,
		index: null,
		currentType: null,
		intrData: null,
	},
	ghostBeat: {
		show: false,
		coordinates: [0, 0],
		type: 0,
		index: "ghost-beat",
	},
	ghostFiducialPoint: {
		show: false,
		sampleIndex: null,
	},
	interval: {
		show: false,
		coordinates: [0, 0, 0, 0],
		type: 0,
		index: "ghost-interval",
	},
	highlight: {
		LeadData: null,
		centerIndex: null,
	},
	mouse: {
		mousedown: false,
		mouseDownOnBeat: false,
		mouseDownOnBeatType: null,
		posX: null,
		posY: null,
		iStart: null,
		bStart: null,
		dragging: null,
	},
	// Signature of the inputs to median-beat computation captured at the time
	// fiducial points were placed (or loaded), bucketed by beat-type slug. If
	// the current computed signature for a bucket no longer matches its saved
	// entry, that bucket's points are stale.
	fiducialPointsSignatureByType: {},
	// Recording id for which RightPanel has restored the saved median-beat
	// window (or confirmed no saved window exists). Consumers that mutate
	// state based on the window — e.g. the fiducial-point prune loop in
	// MedianBeats — must gate on this so they don't act on the slice
	// defaults during the brief interval between "recording loaded" and
	// "window restored." Reset by setAnnotatorState on every load.
	medianBeatWindowLoadedForRecordingId: null,
}

export const Annotator = createSlice({
	name: "@Annotator",
	initialState,
	reducers: {
		init: (state, action) => {
			// Preserve the user's view preferences across recording switches.
			const preservedScale = state.settings.scale
			const preservedMedianBeatSingleColumn = state.settings.medianBeatSingleColumn
			const preservedMedianBeatVerticalSpacing = state.settings.medianBeatVerticalSpacing
			return {
				...initialState,
				settings: {
					...initialState.settings,
					scale: preservedScale,
					medianBeatSingleColumn: preservedMedianBeatSingleColumn,
					medianBeatVerticalSpacing: preservedMedianBeatVerticalSpacing,
				},
			}
		},
		setAnnotatorState: (state, action) => {
			// Fiducial points may arrive in one of three shapes on disk:
			//   3. v3 (current): `annotations.medianBeats` — `{ version: 3, sampleRate, byBeatType: { <slug>: { window, waveforms, fiducialPoints: { pointLabels, ... } }, ... } }`
			//   2. v2: `annotations.medianBeatFiducialPoints` — `{ version: 2, byBeatType: { <slug>: { pointLabels, ... }, ... } }`
			//   1. v1: `annotations.medianBeatFiducialPoints` — single-type object with top-level `pointLabels`
			// Migrate every shape into the `n`-or-other-slug bucket so all
			// consumers can treat storage as { <slug>: [{type, offsetMs}, ...] }.
			const savedMedianBeats = action.payload.annotations?.medianBeats
			const savedFp = action.payload.annotations?.medianBeatFiducialPoints
			let byType = {}
			if (savedMedianBeats && savedMedianBeats.byBeatType && typeof savedMedianBeats.byBeatType === "object") {
				byType = Object.fromEntries(
					Object.entries(savedMedianBeats.byBeatType).map(([slug, entry]) => [
						slug,
						Array.isArray(entry?.fiducialPoints?.pointLabels) ? entry.fiducialPoints.pointLabels : [],
					])
				)
			} else if (savedFp && !Array.isArray(savedFp) && savedFp.byBeatType && typeof savedFp.byBeatType === "object") {
				byType = Object.fromEntries(
					Object.entries(savedFp.byBeatType).map(([slug, entry]) => [
						slug,
						Array.isArray(entry?.pointLabels) ? entry.pointLabels : [],
					])
				)
			} else if (savedFp && Array.isArray(savedFp.pointLabels)) {
				byType = { n: savedFp.pointLabels }
			}
			// Strip empty buckets so downstream length checks are unambiguous.
			byType = Object.fromEntries(
				Object.entries(byType).filter(([, pts]) => Array.isArray(pts) && pts.length > 0)
			)
			const normalizedAnnotations = {
				...action.payload.annotations,
				medianBeatFiducialPointsByType: byType,
			}
			// Remove the raw on-disk fields from the annotations blob so
			// selectors don't accidentally read stale data.
			delete normalizedAnnotations.medianBeatFiducialPoints
			delete normalizedAnnotations.medianBeats
			// Seed the beat-type selection from the dominant-beat-type
			// heuristic. Non-persisted; recomputed per recording.
			const legendBeats = normalizedAnnotations.labels?.beats || []
			const beats = normalizedAnnotations.beats || []
			const defaultBeatType = computeDefaultBeatType(beats, legendBeats)
			const next = {
				...state,
				annotations: normalizedAnnotations,
				filtered: {
					...state.filtered,
					...action.payload.filtered,
					filters: {
						...state.filtered.filters,
						...action.payload.filtered.filters,
					},
				},
				original: action.payload.original,
				leadCount: action.payload.leadCount,
				settings: {
					...state.settings,
					medianBeatBeatType: defaultBeatType,
					medianBeatWindowByType: {},
				},
			};
			// Build a per-type signature for every non-empty bucket so a save
			// without further edits doesn't get flagged as stale.
			const signatureByType = {}
			for (const slug of Object.keys(byType)) {
				signatureByType[slug] = buildFiducialInputSignature(next, slug)
			}
			next.fiducialPointsSignatureByType = signatureByType
			next.medianBeatWindowLoadedForRecordingId = null
			return next;
		},
		setFilteredSamples: (state, action) => {
			state.filtered.samples = action.payload
		},
		setFilters: (state, action) => {
			state.filtered.filters = action.payload
		},
		setRawData: (state, action) => {
			state.rawData = action.payload
		},
		setCurrentTask: (state, action) => {
			state.currentTask = action.payload
		},
		setCurrentTaskRecordings: (state, action) => {
			state.currentTaskRecordings = action.payload
		},
		setCurrentRecordingId: (state, action) => {
			state.currentRecordingId = action.payload
		},
		setScale: (state, action) => {
			state.settings.scale = action.payload
			setPersistedScale(action.payload)
		},
		setInverted: (state, action) => {
			state.settings.inverted = action.payload
		},
		setMedianBeatSingleColumn: (state, action) => {
			state.settings.medianBeatSingleColumn = action.payload
			setPersistedMedianBeatSingleColumn(action.payload)
		},
		setMedianBeatVerticalSpacing: (state, action) => {
			const clamped = Math.min(1, Math.max(0, Number(action.payload)))
			state.settings.medianBeatVerticalSpacing = clamped
			setPersistedMedianBeatVerticalSpacing(clamped)
		},
		setMedianBeatLeftMs: (state, action) => {
			const currentBeatType = state.settings.medianBeatBeatType || "n"
			const currentWindow = state.settings.medianBeatWindowByType[currentBeatType] || DEFAULT_MEDIAN_BEAT_WINDOW
			state.settings.medianBeatWindowByType[currentBeatType] = {
				...currentWindow,
				leftMs: action.payload,
			}
		},
		setMedianBeatRightMs: (state, action) => {
			const currentBeatType = state.settings.medianBeatBeatType || "n"
			const currentWindow = state.settings.medianBeatWindowByType[currentBeatType] || DEFAULT_MEDIAN_BEAT_WINDOW
			state.settings.medianBeatWindowByType[currentBeatType] = {
				...currentWindow,
				rightMs: action.payload,
			}
		},
		// Bulk-restore every beat-type's window (used on recording load).
		// Payload: { [slug]: { leftMs, rightMs }, ... }. Missing/invalid values
		// per bucket fall back to DEFAULT_MEDIAN_BEAT_WINDOW.
		setMedianBeatWindowByType: (state, action) => {
			const clamp = (v) => Number.isFinite(v) ? Math.min(1200, Math.max(200, v)) : null
			const nextByType = {}
			for (const [slug, win] of Object.entries(action.payload || {})) {
				const leftMs = clamp(win?.leftMs) ?? DEFAULT_MEDIAN_BEAT_WINDOW.leftMs
				const rightMs = clamp(win?.rightMs) ?? DEFAULT_MEDIAN_BEAT_WINDOW.rightMs
				nextByType[slug] = { leftMs, rightMs }
			}
			state.settings.medianBeatWindowByType = nextByType
		},
		setMedianBeatWindowLoadedForRecordingId: (state, action) => {
			state.medianBeatWindowLoadedForRecordingId = action.payload
			// The signature captured by setAnnotatorState was built before RightPanel
			// restored the saved inversion/window settings. Now that those have been
			// applied, rebuild the signature for every non-empty bucket against the
			// current settings so a save with no user edits isn't flagged as stale.
			const byType = state.annotations.medianBeatFiducialPointsByType || {}
			const nextSignatures = {}
			for (const slug of Object.keys(byType)) {
				if ((byType[slug] || []).length > 0) {
					nextSignatures[slug] = buildFiducialInputSignature(state, slug)
				}
			}
			state.fiducialPointsSignatureByType = nextSignatures
		},
		selectLead: (state, action) => {
			state.selectedLead = action.payload
		},
		deselectLead: (state, action) => {
			state.selectedLead = null
		},
		addBeat: (state, action) => {
			const { type, beatIndex } = action.payload
			const beatsAtIndex = state.annotations.beats.filter((b) => b.i === beatIndex)
			if (beatsAtIndex.length > 0) {
				// Update the first matching beat's type
				beatsAtIndex[0].t = type
				// Remove any duplicates (keep only the first)
				state.annotations.beats = state.annotations.beats.filter((b) => b.i !== beatIndex)
				state.annotations.beats.push(beatsAtIndex[0])
			} else {
				state.annotations.beats.push({ i: beatIndex, t: type })
			}
			state.lastModifiedBeatIndex = beatIndex
		},
		removeBeat: (state, action) => {
			state.annotations.beats = state.annotations.beats.filter((b) => b.i !== action.payload)
			if (state.lastModifiedBeatIndex === action.payload) {
				state.lastModifiedBeatIndex = null
			}
		},
		modifyBeat: (state, action) => {
			const { beatIndex, newValue } = action.payload
			state.annotations.beats = state.annotations.beats.map((beat) => (beat.i === beatIndex ? { i: newValue[0], t: newValue[1] } : beat))
			state.lastModifiedBeatIndex = newValue[0]
		},
		setAllBeatLabels: (state, action) => {
			state.annotations.beats.forEach((beat) => {
				beat.t = action.payload
			})
		},
		clearAllAnnotations: (state) => {
			state.annotations.beats = []
			state.annotations.intervals = []
			state.annotations.global = []
			state.annotations.doctorNote = ""
		},
		addIntr: (state, action) => {
			state.annotations.intervals.push(action.payload)
		},
		removeIntr: (state, action) => {
			state.annotations.intervals = state.annotations.intervals.filter((b) => b.start !== action.payload)
		},
		updateIntr: (state, action) => {
			const { index, value } = action.payload
			state.annotations.intervals = state.annotations.intervals.map((intr) => (intr.start === index ? { ...intr, ...value } : intr))
		},
		updateIntrContext: (state, action) => {
			state.intrervalMenu = {
				...state.intrervalMenu,
				...action.payload,
			}
		},
		updateContext: (state, action) => {
			state.context = {
				...state.context,
				...action.payload,
			}
		},
		updateGhostBeat: (state, action) => {
			state.ghostBeat = {
				...state.ghostBeat,
				...action.payload,
			}
		},
		updateGhostIntr: (state, action) => {
			state.interval = {
				...state.interval,
				...action.payload,
			}
		},
		updateMouse: (state, action) => {
			state.mouse = {
				...state.mouse,
				...action.payload,
			}
		},
		setMedianBeatBeatType: (state, action) => {
			state.settings.medianBeatBeatType = action.payload
		},
		addFiducialPoint: (state, action) => {
			const { type, offsetMs } = action.payload
			const currentBeatType = state.settings.medianBeatBeatType || "n"
			const byType = state.annotations.medianBeatFiducialPointsByType || {}
			const points = byType[currentBeatType] || []
			const nextPoints = points.filter((fp) => fp.type !== type)
			nextPoints.push({ type, offsetMs })
			state.annotations.medianBeatFiducialPointsByType = {
				...byType,
				[currentBeatType]: nextPoints,
			}
			state.fiducialPointsSignatureByType = {
				...state.fiducialPointsSignatureByType,
				[currentBeatType]: buildFiducialInputSignature(state, currentBeatType),
			}
		},
		removeFiducialPoint: (state, action) => {
			const currentBeatType = state.settings.medianBeatBeatType || "n"
			const byType = state.annotations.medianBeatFiducialPointsByType || {}
			const points = byType[currentBeatType] || []
			const nextPoints = points.filter((fp) => fp.type !== action.payload)
			const nextByType = { ...byType }
			const nextSignatures = { ...(state.fiducialPointsSignatureByType || {}) }
			if (nextPoints.length === 0) {
				delete nextByType[currentBeatType]
				delete nextSignatures[currentBeatType]
			} else {
				nextByType[currentBeatType] = nextPoints
				nextSignatures[currentBeatType] = buildFiducialInputSignature(state, currentBeatType)
			}
			state.annotations.medianBeatFiducialPointsByType = nextByType
			state.fiducialPointsSignatureByType = nextSignatures
		},
		updateFiducialPoint: (state, action) => {
			const { oldType, newType } = action.payload
			const currentBeatType = state.settings.medianBeatBeatType || "n"
			const points = (state.annotations.medianBeatFiducialPointsByType || {})[currentBeatType] || []
			const existing = points.find((fp) => fp.type === oldType)
			if (existing) {
				existing.type = newType
			}
		},
		updateFiducialPointContext: (state, action) => {
			state.fiducialPointContext = {
				...state.fiducialPointContext,
				...action.payload,
			}
		},
		updateGhostFiducialPoint: (state, action) => {
			state.ghostFiducialPoint = {
				...state.ghostFiducialPoint,
				...action.payload,
			}
		},
		setLabelsFiducialPoints: (state, action) => {
			state.annotations.labelsFiducialPoints = action.payload
		},
		loadFiducialPoints: (state, action) => {
			// Loads a flat array of `{type, offsetMs}` into the currently-
			// selected beat-type bucket. Used by callers that pre-compute
			// points and want to seed them under the active beat type.
			const currentBeatType = state.settings.medianBeatBeatType || "n"
			const payload = Array.isArray(action.payload) ? action.payload : []
			const byType = { ...(state.annotations.medianBeatFiducialPointsByType || {}) }
			const signatures = { ...(state.fiducialPointsSignatureByType || {}) }
			if (payload.length > 0) {
				byType[currentBeatType] = payload
				signatures[currentBeatType] = buildFiducialInputSignature(state, currentBeatType)
			} else {
				delete byType[currentBeatType]
				delete signatures[currentBeatType]
			}
			state.annotations.medianBeatFiducialPointsByType = byType
			state.fiducialPointsSignatureByType = signatures
		},
		clearFiducialPoints: (state, action) => {
			// If a payload is provided, treat it as an array of beat-type slugs
			// to clear; otherwise clear the currently-selected type only.
			const byType = { ...(state.annotations.medianBeatFiducialPointsByType || {}) }
			const signatures = { ...(state.fiducialPointsSignatureByType || {}) }
			const targets = Array.isArray(action?.payload) && action.payload.length > 0
				? action.payload
				: [state.settings.medianBeatBeatType || "n"]
			for (const slug of targets) {
				delete byType[slug]
				delete signatures[slug]
			}
			state.annotations.medianBeatFiducialPointsByType = byType
			state.fiducialPointsSignatureByType = signatures
		},
	},
})

export const {
	init,
	setAnnotatorState,
	setFilteredSamples,
	setFilters,
	setRawData,
	setCurrentTask,
	setCurrentTaskRecordings,
	setCurrentRecordingId,
	setScale,
	setInverted,
	setMedianBeatSingleColumn,
	setMedianBeatVerticalSpacing,
	setMedianBeatLeftMs,
	setMedianBeatRightMs,
	setMedianBeatWindowByType,
	setMedianBeatBeatType,
	setMedianBeatWindowLoadedForRecordingId,
	selectLead,
	deselectLead,
	addBeat,
	removeBeat,
	modifyBeat,
	setAllBeatLabels,
	clearAllAnnotations,
	addIntr,
	removeIntr,
	updateIntr,
	updateIntrContext,
	updateContext,
	updateGhostBeat,
	updateGhostIntr,
	updateMouse,
	addFiducialPoint,
	removeFiducialPoint,
	updateFiducialPoint,
	updateFiducialPointContext,
	updateGhostFiducialPoint,
	setLabelsFiducialPoints,
	loadFiducialPoints,
	clearFiducialPoints,
} = Annotator.actions

// Selectors ---------------------------------------------------------------
// Per-type median-beat state. Components should prefer these over reading
// the raw slice fields so they stay resilient to storage-shape changes.

export const selectCurrentBeatType = (state) =>
	state.Annotator?.settings?.medianBeatBeatType || "n"

export const selectCurrentMedianBeatWindow = (state) => {
	const currentBeatType = selectCurrentBeatType(state)
	return (state.Annotator?.settings?.medianBeatWindowByType || {})[currentBeatType] || DEFAULT_MEDIAN_BEAT_WINDOW
}

export const selectFiducialPointsForBeatType = (state, beatType) =>
	(state.Annotator?.annotations?.medianBeatFiducialPointsByType || {})[beatType] || []

export const selectCurrentFiducialPoints = (state) =>
	selectFiducialPointsForBeatType(state, selectCurrentBeatType(state))

export const selectFiducialSignatureForBeatType = (state, beatType) =>
	(state.Annotator?.fiducialPointsSignatureByType || {})[beatType] || null

export const selectCurrentFiducialSignature = (state) =>
	selectFiducialSignatureForBeatType(state, selectCurrentBeatType(state))

export default Annotator.reducer
